<template>
  <TConfirmationModal ref="confirmationModal" class="lang-confirm">
    <template #header>{{ $t("file_manager.warning") }}</template>
    {{ $t("layout.delete_language_confirm") }}
    <template #confirm-action>{{ $t("file_manager.delete") }}</template>
  </TConfirmationModal>
  <TConfirmationModal
    ref="bulkTranslateModal"
    success
    class="bulk-translate-confirm"
  >
    <template #header>{{ $t("layout.translate_all_from_primary") }}</template>
    {{ $t("layout.translate_all_confirm") }}
    <template #confirm-action>{{ $t("layout.translate") }}</template>
  </TConfirmationModal>
  <div
    v-if="!(!languages && isPreview)"
    ref="languageSettings"
    :key="primaryLanguage"
    data-testid="language-settings"
    class="relative font-inter"
  >
    <TButton @click="opened = !opened">
      <template #icon>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="14"
          height="14"
        >
          <path
            d="M352 256c0 22.2-1.2 43.6-3.3 64l-185.3 0c-2.2-20.4-3.3-41.8-3.3-64s1.2-43.6 3.3-64l185.3 0c2.2 20.4 3.3 41.8 3.3 64zm28.8-64l123.1 0c5.3 20.5 8.1 41.9 8.1 64s-2.8 43.5-8.1 64l-123.1 0c2.1-20.6 3.2-42 3.2-64s-1.1-43.4-3.2-64zm112.6-32l-116.7 0c-10-63.9-29.8-117.4-55.3-151.6c78.3 20.7 142 77.5 171.9 151.6zm-149.1 0l-176.6 0c6.1-36.4 15.5-68.6 27-94.7c10.5-23.6 22.2-40.7 33.5-51.5C239.4 3.2 248.7 0 256 0s16.6 3.2 27.8 13.8c11.3 10.8 23 27.9 33.5 51.5c11.6 26 20.9 58.2 27 94.7zm-209 0L18.6 160C48.6 85.9 112.2 29.1 190.6 8.4C165.1 42.6 145.3 96.1 135.3 160zM8.1 192l123.1 0c-2.1 20.6-3.2 42-3.2 64s1.1 43.4 3.2 64L8.1 320C2.8 299.5 0 278.1 0 256s2.8-43.5 8.1-64zM194.7 446.6c-11.6-26-20.9-58.2-27-94.6l176.6 0c-6.1 36.4-15.5 68.6-27 94.6c-10.5 23.6-22.2 40.7-33.5 51.5C272.6 508.8 263.3 512 256 512s-16.6-3.2-27.8-13.8c-11.3-10.8-23-27.9-33.5-51.5zM135.3 352c10 63.9 29.8 117.4 55.3 151.6C112.2 482.9 48.6 426.1 18.6 352l116.7 0zm358.1 0c-30 74.1-93.6 130.9-171.9 151.6c25.5-34.2 45.2-87.7 55.3-151.6l116.7 0z"
            fill="currentColor"
          />
        </svg>
      </template>
      {{
        languages && languages.length > 0
          ? currentLanguageName
          : $t("layout.create_language")
      }}
      <template #iconRight>
        <font-awesome-icon :icon="['fas', 'caret-down']" />
      </template>
    </TButton>
    <div
      v-show="opened"
      data-testid="language-dropdown"
      class="absolute left-[0] top-[calc(100%+1px)] z-[10] box-border w-max pt-1"
      :class="{
        'min-w-[150px]': isPreview,
        'min-w-[300px]': !isPreview,
      }"
    >
      <div
        class="box-border w-max min-w-[100%] max-w-full overflow-hidden rounded-md border border-solid border-gray-500 bg-gray-700 p-2 pb-3"
      >
        <div class="mb-2 flex items-center justify-between gap-2">
          <h3
            class="font-inter text-[13px] font-semibold leading-5 text-gray-50"
          >
            {{ $t("layout.language_settings") }}
          </h3>
          <TButton
            v-if="
              !isPreview &&
              openAiStore.openAiEnabled &&
              languages &&
              languages.length > 1
            "
            v-tooltip="$t('layout.translate_all_from_primary')"
            xs
            data-testid="translate-all-button"
            :loading="generalStore.translationInProgress"
            :disabled="generalStore.translationInProgress"
            @click="translateAll"
          >
            <template #icon>
              <Translate />
            </template>
            {{ $t("layout.translate_all") }}
          </TButton>
        </div>
        <div
          v-if="languages && languages.length > 0"
          class="mb-2 text-xs"
          :class="{
            '!mb-0': isPreview,
          }"
        >
          <VueCustomScrollbar
            ref="langScrollbar"
            style="position: relative; margin: auto"
            :style="{ height: calculatedHeightLangs + 'px' }"
            :settings="{ wheelPropagation: false }"
          >
            <ul class="m-0 list-none p-0">
              <LanguageBox
                v-for="lang of languages"
                :key="lang.key"
                :lang="lang"
                :langs-to-use="langsToUse"
                :is-current-language="currentLanguage === lang.key"
                :is-primary-language="primaryLanguage === lang.key"
                :is-preview="isPreview"
                :languages="languages ?? []"
                :open-ai-enabled="openAiStore.openAiEnabled"
                @delete-language="deleteLanguage(lang.key)"
                @set-primary-language="setPrimaryLanguage(lang.key)"
                @translate-language="
                  (source) => translateLanguage(lang.key, source)
                "
                @click="currentLanguage = lang.key"
              />
            </ul>
          </VueCustomScrollbar>
        </div>
        <TButton
          v-if="
            !creatingLanguage && !isPreview && filteredLangsToUse.length > 0
          "
          data-testid="add-language-button"
          class="w-full"
          @click="startCreatingLanguage"
        >
          <template #icon>
            <Plus />
          </template>
          {{ $t("layout.add_language") }}
        </TButton>
        <LanguageSelector
          v-if="creatingLanguage"
          :langs-to-use="filteredLangsToUse"
          @select="createLanguage"
          @cancel="creatingLanguage = false"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import LanguageSelector from "./LanguageSelector.vue";
  import LanguageBox from "./LanguageBox.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useOpenAiStore } from "@/stores/openAiStore";
  import { usePreviewStore } from "@/stores/preview";
  import { useNotificationsStore } from "@/stores/notifications";
  import { useGeneralStore } from "@/stores/generalStore";
  import usePreview from "@/composables/usePreview";
  import { TopolLanguages } from "@/schemas/template/languageSchema";
  import { IMultilingualLanguage } from "@/schemas/language/languageSchema";

  import { TButton, TConfirmationModal } from "@topol/twig-ui";
  import { Plus, Translate } from "@topol/icons";
  import { iso639 } from "@topol/utils";

  import { useI18n } from "vue-i18n";
  import { onClickOutside } from "@vueuse/core";
  import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
  import { computed, ref, onMounted, onBeforeUnmount, nextTick } from "vue";

  const mjmlStore = useMjmlStore();
  const previewStore = usePreviewStore();
  const notificationsStore = useNotificationsStore();
  const generalStore = useGeneralStore();
  const openAiStore = useOpenAiStore();
  const { setPreview } = usePreview();
  const { t } = useI18n();

  const languageSettings = ref<HTMLElement | null>(null);
  const opened = ref(false);
  const confirmationModal = ref();
  const bulkTranslateModal = ref();
  const creatingLanguage = ref(false);
  const langScrollbar = ref();

  onClickOutside(languageSettings, closeOnClickOutside, {
    ignore: [
      ".lang-confirm",
      ".lang-confirm *",
      ".bulk-translate-confirm",
      ".bulk-translate-confirm *",
      ".translate-from-popper",
      ".translate-from-popper *",
    ],
  });

  const isPreview = computed(() => {
    return previewStore.previewMode;
  });

  const langsToUse = computed<IMultilingualLanguage>(() => {
    if (generalStore.customLanguagePreset) {
      return generalStore.customLanguagePreset;
    }
    return iso639;
  });

  const filteredLangsToUse = computed(() => {
    return langsToUse.value.filter(
      (lang) => !languagesCodes.value.includes(lang.code)
    );
  });

  const languages = computed(() => {
    const availableLanguages = mjmlStore.mjml.langs;
    if (!availableLanguages) return availableLanguages;
    const sorted: TopolLanguages = JSON.parse(
      JSON.stringify(availableLanguages)
    );
    return sorted.sort((a, b) => {
      if (a.primary === b.primary) return 0;
      return a.primary ? -1 : 1;
    });
  });

  const languagesCodes = computed(() => {
    if (!languages.value) return [];
    return languages.value.map((lang) => lang.key);
  });

  const calculatedHeightLangs = computed(() => {
    const height =
      languagesCodes.value.length * 52 + (languagesCodes.value.length - 1) * 8;

    if (height > 320) {
      return 320;
    }

    return height;
  });

  const currentLanguage = computed({
    get() {
      return mjmlStore.langToShow;
    },
    set(value: string) {
      if (
        value === currentLanguage.value ||
        !languages.value?.find((l) => l.key === value)
      ) {
        return;
      }
      mjmlStore.langToShow = value;
      mjmlStore.emitLanguageCallback("onLanguageSelected", [value]);
      if (isPreview.value) {
        setPreview();
      }
    },
  });

  const currentLanguageName = computed(() => {
    if (!currentLanguage.value) return "";
    const lang = langsToUse.value.find((l) => l.code === currentLanguage.value);
    return lang ? lang.name : currentLanguage.value;
  });

  const primaryLanguage = computed({
    get() {
      return languages.value?.find((l) => l.primary)?.key || "";
    },
    set(value: string) {
      if (value === primaryLanguage.value) {
        return;
      }
      mjmlStore.changePrimaryLanguage(value);
    },
  });

  function createLanguage(lang: string) {
    if (!lang || lang.trim() === "") return;
    if (languages.value?.find((l) => l.key === lang)) {
      notificationsStore.createNotification({
        type: "error",
        text: t("layout.language_exists"),
      });
      return;
    }
    mjmlStore.createLanguage(lang.trim());
    creatingLanguage.value = false;
    if (langScrollbar.value) {
      setTimeout(() => {
        langScrollbar.value.$el.scrollTop =
          langScrollbar.value.$el.scrollHeight;
      }, 30);
    }
  }

  function setPrimaryLanguage(lang: string) {
    if (lang === primaryLanguage.value) return;
    primaryLanguage.value = lang;
    if (langScrollbar.value) {
      langScrollbar.value.$el.scrollTop = 0;
    }
  }

  async function deleteLanguage(lang: string) {
    if (lang === primaryLanguage.value) {
      notificationsStore.createNotification({
        type: "error",
        text: t("layout.cannot_delete_primary_language"),
      });
      return;
    }
    if (!languages.value?.find((l) => l.key === lang)) {
      notificationsStore.createNotification({
        type: "error",
        text: t("layout.language_not_found"),
      });
      return;
    }
    const confirmation = await confirmationModal.value.open();

    if (confirmation === "cancel") {
      return;
    }
    mjmlStore.deleteLanguage(lang);
  }

  function escapeByKey(event: KeyboardEvent) {
    if (event.key === "Escape") {
      opened.value = false;
      creatingLanguage.value = false;
    }
  }

  function closeOnClickOutside() {
    opened.value = false;
    creatingLanguage.value = false;
  }

  function selectLanguage(lang: string) {
    currentLanguage.value = lang;
  }

  function translateLanguage(lang: string, sourceLang?: string) {
    currentLanguage.value = lang;
    if (!opened.value || !lang || lang.trim() === "") return;
    const langObject = langsToUse.value.find((l) => l.code === lang);
    openAiStore.translateText(
      langObject ? `${langObject.name} (${langObject.code})` : lang,
      sourceLang
    );
    opened.value = false;
  }

  async function translateAll() {
    if (generalStore.translationInProgress) return;
    opened.value = false;
    const confirmation = await bulkTranslateModal.value.open();
    if (confirmation === "cancel") return;
    await openAiStore.translateAllFromPrimary(langsToUse.value);
  }

  async function startCreatingLanguage() {
    creatingLanguage.value = true;
    await nextTick();
    const input = languageSettings.value?.querySelector("input");
    input?.focus();
  }

  onMounted(() => {
    document.addEventListener("keydown", escapeByKey);
    window.$event.on("iframeClick", closeOnClickOutside);
    window.$event.on("createLang", createLanguage);
    window.$event.on("setPrimaryLang", setPrimaryLanguage);
    window.$event.on("selectLang", selectLanguage);
    window.$event.on("deleteLang", deleteLanguage);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", escapeByKey);
    window.$event.off("iframeClick", closeOnClickOutside);
    window.$event.off("createLang", createLanguage);
    window.$event.off("setPrimaryLang", setPrimaryLanguage);
    window.$event.off("selectLang", selectLanguage);
    window.$event.off("deleteLang", deleteLanguage);
  });
</script>
